import type { HierarchicalItem } from "../../types/uiTypes";
import { ConfirmationModal } from "./ConfirmationModal";

type DeleteConfirmationModalProps<T extends HierarchicalItem> = {
  item: T;
  singularName: string;
  onClose: () => void;
  onConfirm: (item: T) => Promise<void>;
};

export function DeleteConfirmationModal<T extends HierarchicalItem>({
  item,
  singularName,
  onClose,
  onConfirm,
}: DeleteConfirmationModalProps<T>) {
  // Flatten all descendants
  const getAllDescendants = (node: T): T[] => {
    const descendants: T[] = [];
    if (node.children) {
      for (const child of node.children) {
        descendants.push(child as T);
        descendants.push(...getAllDescendants(child as T));
      }
    }
    return descendants;
  };

  const descendants = getAllDescendants(item);
  const totalItems = descendants.length + 1; // +1 for the item itself

  // Build affected items list
  const affectedItems = [
    { id: item.id, name: item.name, code: item.code },
    ...descendants.map(d => ({ id: d.id, name: d.name, code: d.code })),
  ];

  const message = `Are you sure you want to delete ${item.name}${item.code ? ` (${item.code})` : ""}?`;
  const warningMessage =
    descendants.length > 0
      ? `This action cannot be undone. This will delete ${totalItems} item${totalItems > 1 ? "s" : ""} total.`
      : `This action cannot be undone. This will permanently remove the ${singularName}.`;

  return (
    <ConfirmationModal
      title={`Delete ${singularName}`}
      message={message}
      warningMessage={warningMessage}
      affectedItems={descendants.length > 0 ? affectedItems : undefined}
      confirmButtonText="Delete"
      confirmButtonColor="error"
      cancelButtonText="Cancel"
      onClose={onClose}
      onConfirm={() => onConfirm(item)}
    />
  );
}
